import { Box, Card, CardContent, Chip, Divider, LinearProgress, Stack, Tooltip, Typography } from "@mui/material";
import { Groups, ArrowForward } from "@mui/icons-material";
import { Link } from "react-router-dom";
import { useGetStudentsQuery } from "../../services/students.service";
import { useGetTasksQuery } from "../../services/tasks.service";
import { useGetSubmissionsQuery } from "../../services/submissions.service";
import { useGetUsersQuery } from "../../services/users.service";
import { useAppTheme } from "../../context/ThemeContext";

const ACTIVE_STUDENT_STATUSES = new Set([
  "initial", "visa", "visa:reminder", "visa:preparing", "visa:submitted",
  "admission:reminder", "admission:preparing", "admission:submitted",
  "issue resolution", "registration", "registration:reminder",
  "deferment:preparing", "deferment:submitted", "pickup:reminder", "commission:pending",
]);

function EmployeeWorkloadWidget() {
  const { data: students } = useGetStudentsQuery("Students");
  const { data: tasks } = useGetTasksQuery("Tasks");
  const { data: submissions } = useGetSubmissionsQuery("Submissions");
  const { data: users } = useGetUsersQuery("users");
  const { isNewTheme, accentColor } = useAppTheme();

  const employees = (users ?? []).filter((u) => u.role !== "admin");

  type Row = { id: string; name: string; activeStudents: number; openTasks: number; pendingSubmissions: number; total: number };

  const rows: Row[] = employees.map((u) => {
    const activeStudents = (students ?? []).filter(
      (s) => s.employee === u._id && ACTIVE_STUDENT_STATUSES.has(s.status)
    ).length;
    const openTasks = (tasks ?? []).filter(
      (t) => t.assignedTo === u._id && t.status !== "Completed"
    ).length;
    const pendingSubmissions = (submissions ?? []).filter(
      (s) => s.employee === u._id && s.status !== "completed" && s.status !== "rejected"
    ).length;
    return { id: u._id, name: u.username, activeStudents, openTasks, pendingSubmissions, total: activeStudents + openTasks + pendingSubmissions };
  }).sort((a, b) => b.total - a.total);

  const maxTotal = Math.max(...rows.map((r) => r.total), 1);

  const cardSx = isNewTheme
    ? {
        width: "100%",
        borderRadius: "8px",
        border: "1px solid #E2E8F0",
        boxShadow: "0 2px 6px rgba(15,23,42,0.05)",
        transition: "box-shadow 0.15s ease",
        "&:hover": { boxShadow: "0 4px 16px rgba(15,23,42,0.07)" },
      }
    : { width: "100%", borderRadius: "10px", boxShadow: "0px 0px 55px 5px rgba(0,0,0,0.05)" };

  return (
    <Card sx={cardSx}>
      <Box sx={{ px: 2.5, pt: 2, pb: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {isNewTheme && (
            <Box sx={{ width: 32, height: 32, borderRadius: "8px", bgcolor: `${accentColor}1a`, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Groups sx={{ fontSize: 18, color: accentColor }} />
            </Box>
          )}
          <Box>
            <Typography variant="h6" fontWeight={isNewTheme ? 700 : 600} color={isNewTheme ? "text.primary" : "black"}>
              Employee Workload
            </Typography>
            {isNewTheme && <Box sx={{ width: 32, height: 3, borderRadius: 999, bgcolor: accentColor, mt: 0.5 }} />}
          </Box>
        </Box>
        <Link to="/students" style={{ textDecoration: "none" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.3, color: "primary.main", fontSize: "0.75rem", fontWeight: 600, opacity: 0.85, "&:hover": { opacity: 1 } }}>
            View students <ArrowForward sx={{ fontSize: 13 }} />
          </Box>
        </Link>
      </Box>

      <Divider sx={isNewTheme ? { borderColor: "#E2E8F0" } : {}} />

      <CardContent sx={{ px: 2.5, py: 2 }}>
        {rows.length === 0 ? (
          <Typography variant="body2" color="text.disabled" sx={{ textAlign: "center", py: 2, fontStyle: "italic" }}>
            No employees found
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            {/* Legend */}
            <Stack direction="row" spacing={1.5} sx={{ mb: 0.5 }}>
              {[
                { label: "Students", color: accentColor },
                { label: "Tasks", color: "#F59E0B" },
                { label: "Submissions", color: "#8B5CF6" },
              ].map(({ label, color }) => (
                <Stack key={label} direction="row" alignItems="center" spacing={0.4}>
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: color }} />
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem" }}>{label}</Typography>
                </Stack>
              ))}
            </Stack>

            {rows.map((row) => (
              <Box key={row.id}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={0.5}>
                  <Typography variant="body2" fontWeight={600} sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "55%" }}>
                    {row.name}
                  </Typography>
                  <Stack direction="row" spacing={0.5}>
                    <Tooltip title={`${row.activeStudents} active students`} arrow>
                      <Chip label={row.activeStudents} size="small" sx={{ height: 18, fontSize: "0.6rem", fontWeight: 700, bgcolor: accentColor + "20", color: accentColor, border: `1px solid ${accentColor}40` }} />
                    </Tooltip>
                    <Tooltip title={`${row.openTasks} open tasks`} arrow>
                      <Chip label={row.openTasks} size="small" sx={{ height: 18, fontSize: "0.6rem", fontWeight: 700, bgcolor: "#FEF3C720", color: "#F59E0B", border: "1px solid #F59E0B40" }} />
                    </Tooltip>
                    <Tooltip title={`${row.pendingSubmissions} pending submissions`} arrow>
                      <Chip label={row.pendingSubmissions} size="small" sx={{ height: 18, fontSize: "0.6rem", fontWeight: 700, bgcolor: "#EDE9FE20", color: "#8B5CF6", border: "1px solid #8B5CF640" }} />
                    </Tooltip>
                  </Stack>
                </Stack>

                {/* Stacked workload bar */}
                <Tooltip title={`Total workload: ${row.total} items`} arrow>
                  <Box sx={{ height: 6, borderRadius: 999, bgcolor: "#F1F5F9", overflow: "hidden", display: "flex" }}>
                    <Box sx={{ width: `${(row.activeStudents / maxTotal) * 100}%`, bgcolor: accentColor, transition: "width 0.4s ease" }} />
                    <Box sx={{ width: `${(row.openTasks / maxTotal) * 100}%`, bgcolor: "#F59E0B", transition: "width 0.4s ease" }} />
                    <Box sx={{ width: `${(row.pendingSubmissions / maxTotal) * 100}%`, bgcolor: "#8B5CF6", transition: "width 0.4s ease" }} />
                  </Box>
                </Tooltip>
              </Box>
            ))}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

export default EmployeeWorkloadWidget;
